import React from "react";

import PropTypes from "prop-types";
import { Navigate } from "react-router-dom";

import useOrder from "hooks/useOrder";
import useRoutePaths from "hooks/useRoutePaths";
import { useAuthentication } from "libs/authentication";
import {
  CART_WALLET_SELECTION,
  FAST_TOP_UP,
  HOME,
  LOGIN,
  LOGIN_AKAMAI_CALLBACK,
  LOGIN_CAS_CALLBACK,
  LOGIN_REACH_FIVE_CALLBACK,
  PASSWORD_RESET,
  PASSWORD_RESET_CONFIRM,
  SHOPS,
  SIGN_UP,
} from "routers/routes";

/**
 * This component is a wrapper to use mainly in routers to manage pages access authorizations.
 * That allow us to filter users and give access to pages only for authenticated or not authenticated user for example.
 * If the access is refused, then we allow to define an action to trigger (to make a redirection dor example).
 * By default, the user is redirect either to /login page (if he must be authenticated) or /home (if he must not be).
 * But later, we could add other kind of authorization (user groups...).
 * Note there is a second level of check which is related to other rules (and not related to authentication)
 * It can be for example the type of widget or something like that.
 * The rule and the action to do when the rule is not fulfilled is provided by props to this component.
 * At the end this filter:
 * - firstly check whether the user is allowed according to what is provided in props
 * (external context managed by the app in parent components)
 * - secondly check whether the user is authorized according to the authentication rules defined by the props
 * (internal context about whether the user is authenticated (logged in) or not).
 */
const RouteFilterWidgetConfig = ({
  widget,
  route = null,
  isCashlessAllowed = false,
  isFastTopUpAllowed = false,
  isPreorderAllowed = false,
  isPreorderCartFlow = false,
  children = null,
}) => {
  const { isAuthenticated } = useAuthentication();
  const { walletId, refundWalletId } = useOrder();
  const routePaths = useRoutePaths();

  const isCashlessWidget = widget?.cashless_config?.is_enabled;
  const isFastTopUpWidget = widget?.fast_topup_config?.is_enabled;
  const isPreorderWidget = widget?.preorder_config?.is_enabled;
  const isPreorderCashlessPaymentAvailable = widget?.preorderConfig?.paymentModes?.includes("CASHLESS");
  const isPreorderOnlinePaymentAvailable = widget?.preorderConfig?.paymentModes?.includes("ONLINE");
  const isPreorderAccountRequired = !widget?.preorderConfig?.isNoAccountAllowed;

  const mustSelectWallet = (
    isPreorderWidget
    && isPreorderCartFlow
    && isPreorderCashlessPaymentAvailable
    && !walletId
    && walletId !== 0
    && !refundWalletId
  );

  const mustBeAuthenticated = (
    isPreorderWidget
    && (!isPreorderOnlinePaymentAvailable || isPreorderAccountRequired)
    && ![
      LOGIN,
      LOGIN_AKAMAI_CALLBACK,
      LOGIN_CAS_CALLBACK,
      LOGIN_REACH_FIVE_CALLBACK,
      SIGN_UP,
      PASSWORD_RESET,
      PASSWORD_RESET_CONFIRM,
    ].includes(route)
  );

  if (
    ![isCashlessAllowed, isPreorderAllowed, isFastTopUpAllowed].some((el) => el)
    || ![isCashlessWidget, isFastTopUpWidget, isPreorderWidget].some((el) => el)
  ) {
    // eslint-disable-next-line no-console
    console.error("Wrong config. Please check the route filter configuration or set at least one type of widget");
    return null;
  }

  // When the widget is a fast top-up widget, we cannot have any other type of widget.
  // So the route MUST be allowed otherwise we redirect to the fast top-up default page.
  // TODO: when redirect to 'LOGIN', manage redirection back to the page
  if (isFastTopUpWidget && !isFastTopUpAllowed) {
    return <Navigate to={routePaths[FAST_TOP_UP]()} />;
  }

  if (isPreorderWidget) {
    if (!isPreorderAllowed) {
      if (isCashlessWidget) {
        if (!isCashlessAllowed) {
          return <Navigate to={routePaths[HOME]()} />;
        }
      } else {
        return <Navigate to={routePaths[SHOPS]()} />;
      }
    }
    if (mustBeAuthenticated && !isAuthenticated) {
      return <Navigate to={routePaths[LOGIN]()} />;
    }
    if (mustSelectWallet) {
      return <Navigate to={routePaths[CART_WALLET_SELECTION]()} />;
    }
  }

  if (isCashlessWidget && !isCashlessAllowed) {
    return <Navigate to={routePaths[HOME]()} />;
  }

  return children;
};

RouteFilterWidgetConfig.propTypes = {
  /**
   * The widget data that describes the config returned by the API.
   */
  widget: PropTypes.object.isRequired,
  /**
   * The key of the route wrapped.
   */
  route: PropTypes.string,
  /**
   * Whether the route is allowed for widget of type cashless.
   */
  isCashlessAllowed: PropTypes.bool,
  /**
   * Whether the route is allowed for widget of type fast top-up.
   */
  isFastTopUpAllowed: PropTypes.bool,
  /**
   * Whether the route is allowed for widget of type preorder.
   */
  isPreorderAllowed: PropTypes.bool,
  /**
   * Whether the route is related to the flow of cart in preorder.
   * When true, we must ensure we have the wallet selected when the payment type is set to cashless.
   */
  isPreorderCartFlow: PropTypes.bool,
  /**
   * The components to render when the authentication rules defined are fulfilled.
   */
  children: PropTypes.node,
};

export default RouteFilterWidgetConfig;
